import React, { useState, useEffect, useRef } from "react";
import WordImage from "./WordImage";
const shuffleArray = (array) => {
  const newArray = [...array];
  for (let i = newArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
  }
  return newArray;
};

const MultipleChoice = ({ words, language, labels, onScore, category, setInProgress, onViewResults }) => {
  const [currentWord, setCurrentWord] = useState({});
  const [options, setOptions] = useState([]);
  const [feedback, setFeedback] = useState("");
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [brokenImages, setBrokenImages] = useState(new Set());
  const [filteredWords, setFilteredWords] = useState([]);
  const [roundWords, setRoundWords] = useState([]);
  const containerRef = useRef(null);

  useEffect(() => {
    let filtered = [];
    try {
      const wordlist = require(`../data/wordlist.${language}.json`);
      const wordlist_de = require("../data/wordlist.de.json");
      if (category && wordlist[category]) {
        filtered = wordlist[category].map((w, i) => ({
          translated: w,
          base: wordlist_de[category]?.[i] || w
        }));
      } else {
        const flatTranslated = Object.values(wordlist).flat();
        const flatBase = Object.values(wordlist_de).flat();
        filtered = flatTranslated.map((w, i) => ({
          translated: w,
          base: flatBase[i] || w
        }));
      }
    } catch (e) {
      console.error("Failed to load wordlist:", e);
    }
    setFilteredWords(filtered);
  }, [words, language, category]);

  // Only start a new round when filteredWords changes and roundWords is empty
  useEffect(() => {
    if (filteredWords.length && roundWords.length === 0) {
      startNewRound();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredWords]);

  // Reset round state when category or language change
  useEffect(() => {
    setRoundWords([]);
    setQuestionIndex(0);
    setScore(0);
    if (setInProgress) setInProgress(false);
  }, [category, language]);

  const startNewRound = () => {
    setRoundWords(shuffleArray(filteredWords).slice(0, 10));
    setQuestionIndex(0);
    setScore(0);
    if (setInProgress) setInProgress(true);
  };

  useEffect(() => {
    if (roundWords.length && questionIndex < roundWords.length) {
      const correctWord = roundWords[questionIndex];
      const distractorsPool = filteredWords.filter(
        (w) => w.translated.toLowerCase() !== correctWord.translated.toLowerCase()
      );
      const distractors = shuffleArray(distractorsPool).slice(0, 3);
      const allOptions = shuffleArray([...distractors, correctWord]);
      setCurrentWord(correctWord);
      setOptions(allOptions);
      setFeedback("");
      setSelectedAnswer(null);
      setBrokenImages(new Set());
    }
  }, [roundWords, filteredWords, questionIndex]);

  // Scroll to center the question/game area on each new question
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [questionIndex, roundWords]);

  useEffect(() => {
    if (questionIndex > 0 && questionIndex === roundWords.length && onScore) {
      onScore(score);
      if (setInProgress) setInProgress(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionIndex, roundWords]);

  const handleAnswer = (selected) => {
    setSelectedAnswer(selected);
    if (selected.translated.trim().toLowerCase() === currentWord.translated.trim().toLowerCase()) {
      setFeedback(`${labels.correctRead}! 🎉`);
      setScore(prev => prev + 1);
    } else {
      setFeedback(`😢 ${labels.tryAgain}`);
    }
    setTimeout(() => {
      setQuestionIndex((prev) => prev + 1);
    }, 1500);
  };

  return questionIndex >= roundWords.length ? (
    <div className="multiple-choice" ref={containerRef}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
        <h2>{labels.result}</h2>
        <p>{labels.correctRead}: {score} {labels.of} {roundWords.length}</p>
        {score >= 8 && <h3>🎉 {labels.amazing}</h3>}
        {score >= 5 && score < 8 && <h3>😊 {labels.good}</h3>}
        {score >= 3 && score < 5 && <h3>😐 {labels.couldDoBetter}</h3>}
        {score < 3 && <h3>😢 {labels.tryAgain}</h3>}
        <button onClick={startNewRound} className="green-button" style={{ marginTop: 24 }}>
          {labels.newGame || 'New Game'}
        </button>
        <button onClick={onViewResults} className="small-button" style={{ marginTop: 16 }}>
          {labels.viewResults || 'View Results'}
        </button>
      </div>
    </div>
  ) : (
    <div className="multiple-choice" ref={containerRef}>
      <h2>{labels.question}</h2>
      <p>{labels.word} {questionIndex + 1} {labels.of} {roundWords.length}</p>
      <h3 style={{ fontSize: '2.5em', fontWeight: 700 }}>{currentWord.translated}</h3>
      <div className="options-grid responsive-options-grid" style={{ gap: 4 }}>
        {options.map((word, index) => {
          let className = "";
          if (selectedAnswer) {
            if (word.translated === currentWord.translated && selectedAnswer.translated === word.translated) className = "correct-answer";
            else if (selectedAnswer.translated === word.translated) className = "wrong-answer";
          }
          return (
            <button
              key={word.translated}
              onClick={() => handleAnswer(word)}
              className={className}
              disabled={!!selectedAnswer}
              style={{ margin: 0 }}
            >
              <WordImage word={word.translated} language={language} category={"multiple_choice"} maxWidthPct={48} />
            </button>
          );
        })}
      </div>
      <p>{feedback}</p>
    </div>
  );
};

export default MultipleChoice;
